/**
 * rosterGuardTick — garde-fou anti-disparition de chauffeurs (24/07/2026).
 *
 * Retour user prod (récurrent) : des chauffeurs sortent des effectifs sans
 * aucune annonce. Tous les chemins de départ LÉGITIMES du jeu archivent le
 * chauffeur dans `firedDriversArchive` (licenciement, démission, attrition,
 * préavis expiré, événements, messagerie). Ce tick compare donc l'effectif
 * courant au registre de la veille (`_rosterLedger`) :
 *   → chauffeur absent SANS archive de départ = anomalie
 *   → restauration automatique + alerte rouge + email RH.
 *
 * Le registre conserve un snapshot complet de chaque chauffeur pour pouvoir
 * le restaurer à l'identique (niveau, formations, salaire...).
 */
export function runRosterGuardTick(get, set) {
  const state = get();
  if (!state.gameStarted) return { restored: [] };

  const ledger = state._rosterLedger || {};
  const roster = state.drivers || [];
  const currentIds = new Set(roster.map(d => d.id));
  const rosterDataIds = new Set(roster.map(d => d.dataId).filter(Boolean));
  const archive = state.firedDriversArchive || {};
  const today = state.totalDays || 0;

  const missing = Object.values(ledger).filter(entry =>
    entry && entry.driver
    && !currentIds.has(entry.id)
    && !archive[entry.dataId]        // départ légitime → archivé
    && !rosterDataIds.has(entry.dataId) // ré-embauché même jour (nouvel id)
  );

  if (missing.length > 0) {
    const restoredDrivers = missing.map(entry => ({
      ...entry.driver,
      status: 'idle',
      assignedVehicleId: null,
      onHoliday: false,
    }));
    const names = restoredDrivers.map(d => d.name).join(', ');
    const plural = missing.length > 1;
    console.warn('[rosterGuard] Disparition anormale détectée — restauration :', names);
    set({
      drivers: [...get().drivers, ...restoredDrivers],
      notifications: [...(get().notifications || []).slice(-30), {
        id: `roster-guard-${today}-${missing.length}`,
        type: 'roster_anomaly',
        title: `⚠️ Anomalie effectif — ${missing.length} chauffeur${plural ? 's' : ''} restauré${plural ? 's' : ''}`,
        message: `${names} ${plural ? 'avaient disparu' : 'avait disparu'} de l'effectif sans départ enregistré (ni démission, ni licenciement, ni préavis). Restauration automatique effectuée (statut : disponible au dépôt).`,
        day: today,
        read: false,
      }],
    });
    if (typeof get().pushCustomMessage === 'function') {
      try {
        get().pushCustomMessage({
          kind: 'info',
          senderType: 'hr',
          senderName: 'Service RH',
          tone: 'urgent',
          subject: `Anomalie effectif corrigée — ${names}`,
          body: `Bonjour,\n\nNotre pointage quotidien a détecté ${plural ? 'des disparitions anormales' : 'une disparition anormale'} dans l'effectif : **${names}** ${plural ? 'ne figuraient' : 'ne figurait'} plus au registre du personnel alors qu'aucun départ (démission, licenciement, fin de préavis) n'a été enregistré.\n\n${plural ? 'Ils ont été réintégrés' : 'Il/Elle a été réintégré(e)'} automatiquement avec ${plural ? 'leurs' : 'ses'} acquis (niveau, formations, salaire). ${plural ? 'Leurs affectations véhicule ont' : 'Son affectation véhicule a'} été remise à zéro par précaution — pensez à ${plural ? 'les' : 'le/la'} réaffecter.\n\n— Service RH`,
          ttlDays: 10,
        });
      } catch (err) { console.warn('[rosterGuard] email push failed:', err); }
    }
  }

  // Rafraîchit le registre depuis l'effectif courant (post-restauration).
  const nextLedger = {};
  (get().drivers || []).forEach(d => {
    nextLedger[d.id] = { id: d.id, dataId: d.dataId, name: d.name, day: today, driver: d };
  });
  set({ _rosterLedger: nextLedger });

  return { restored: missing.map(m => m.name) };
}
